import { t } from '@lingui/core/macro';
import { assertUnreachable } from '@luminovo/commons';
import { List, ListItem, ListItemText, styled } from '@mui/material';
import { ArrowLeftIcon } from 'lucide-react';
import React from 'react';

import { type AppLinkOptions, useBackNavigation, useCanOpen } from '../../routing';
import { colorSystem } from '../../theme';
import { TertiaryIconButton } from '../buttons';
import { Flexbox } from '../Flexbox';
import { NavListItem } from './NavListItem';
export type NavigationSidebarLinkHeader = { type: 'header'; label: React.ReactNode; hidden?: boolean };
export type NavigationSidebarLinkItem = {
    type: 'link';
    label: string | JSX.Element;
    /** Build it with `linkOptions()`. */
    link: AppLinkOptions;
    disabled?: boolean;
    hidden?: boolean;
};
type NavigationSidebarItem = NavigationSidebarLinkHeader | NavigationSidebarLinkItem;

export interface NavigationSidebarProps {
    title: string;
    contents: Array<NavigationSidebarItem>;
    /** Where the back arrow goes when there's nowhere in the app to go back to; build it with `linkOptions()`. */
    back: AppLinkOptions;
}

export function NavigationSidebar({ title, contents, back }: NavigationSidebarProps): JSX.Element {
    const { navigateBack } = useBackNavigation();
    const canOpen = useCanOpen();

    const shows = (item: NavigationSidebarItem) => !item.hidden && (item.type === 'header' || canOpen(item.link));
    const visibleContents = contents.filter((item, index) => {
        if (!shows(item)) {
            return false;
        }
        if (item.type === 'link') {
            return true;
        }
        // A header shows only above a link that shows, before the next header.
        const below = contents.slice(index + 1);
        const nextHeader = below.findIndex((next) => next.type === 'header');
        return below.slice(0, nextHeader === -1 ? undefined : nextHeader).some(shows);
    });
    return (
        <StyledList>
            <ListItem>
                <Flexbox
                    gap={8}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <TertiaryIconButton
                        size="small"
                        aria-label={t`Back`}
                        onClick={() => navigateBack({ fallback: back })}
                        style={{ padding: 0, marginRight: 4 }}
                    >
                        <ArrowLeftIcon />
                    </TertiaryIconButton>

                    <ListItemText
                        style={{
                            paddingLeft: '0px',
                            color: colorSystem.neutral[8],
                            cursor: 'normal',
                        }}
                        primary={title}
                        slotProps={{
                            primary: { variant: 'h2', noWrap: true },
                        }}
                    />
                </Flexbox>
            </ListItem>
            {visibleContents.map((link, i) => {
                return <NavigationSidebarLink key={i} link={link} />;
            })}
            {/*for some reason adding a margin/padding to the StyledList did not work and the last <NavigationSidebarLink /> is always hidden
              I suspect this has something to do with the Navigation component, but not sure how. The div below ensures all links will be visible
             */}
            <div style={{ height: '60px' }} />
        </StyledList>
    );
}

function NavigationSidebarLink({ link }: { link: NavigationSidebarItem }) {
    if (link.type === 'header') {
        return (
            <ListItem style={{ background: colorSystem.neutral[0] }}>
                <ListItemText
                    primary={link.label}
                    slotProps={{
                        primary: { style: headerStyles },
                    }}
                />
            </ListItem>
        );
    }
    if (link.type === 'link') {
        return <NavListItem link={link.link} label={link.label} disabled={link.disabled} />;
    }
    assertUnreachable(link);
}

const StyledList = styled(List)({
    background: colorSystem.neutral[0],
    height: '100%',
    boxSizing: 'border-box',
});

const headerStyles: React.CSSProperties = {
    fontWeight: 600,
    color: colorSystem.neutral[8],
};
